import { useState } from "react";

type Role = "seller" | "buyer" | "influencer";
type Mode = "login" | "signup";
type LoginMethod = "email" | "phone";

interface Props {
  onClose: () => void;
  onAuth: (user: { name: string; email: string; role: Role; phone?: string }) => void;
  defaultRole?: Role;
}

const roleOptions: { key: Role; label: string; icon: string; desc: string; color: string }[] = [
  { key: "seller", label: "Factory / Seller", icon: "ri-store-2-line", desc: "Post & sell products", color: "border-amber-400 bg-amber-50 text-amber-600" },
  { key: "buyer", label: "Buyer", icon: "ri-shopping-cart-line", desc: "Browse & order", color: "border-stone-400 bg-stone-50 text-stone-700" },
  { key: "influencer", label: "Influencer", icon: "ri-star-line", desc: "Promote & earn", color: "border-rose-400 bg-rose-50 text-rose-600" },
];

const countryCodes = [
  { code: "+1", flag: "🇺🇸", name: "USA" },
  { code: "+1", flag: "🇨🇦", name: "Canada" },
  { code: "+44", flag: "🇬🇧", name: "UK" },
  { code: "+49", flag: "🇩🇪", name: "Germany" },
  { code: "+33", flag: "🇫🇷", name: "France" },
  { code: "+86", flag: "🇨🇳", name: "China" },
  { code: "+81", flag: "🇯🇵", name: "Japan" },
  { code: "+82", flag: "🇰🇷", name: "Korea" },
  { code: "+91", flag: "🇮🇳", name: "India" },
  { code: "+971", flag: "🇦🇪", name: "UAE" },
  { code: "+61", flag: "🇦🇺", name: "Australia" },
  { code: "+65", flag: "🇸🇬", name: "Singapore" },
  { code: "+55", flag: "🇧🇷", name: "Brazil" },
  { code: "+52", flag: "🇲🇽", name: "Mexico" },
];

export default function AuthModal({ onClose, onAuth, defaultRole = "buyer" }: Props) {
  const [mode, setMode] = useState<Mode>("login");
  const [role, setRole] = useState<Role>(defaultRole);
  const [loginMethod, setLoginMethod] = useState<LoginMethod>("email");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [countryCode, setCountryCode] = useState("+1");
  const [password, setPassword] = useState("");
  const [company, setCompany] = useState("");
  const [warehouseLocation, setWarehouseLocation] = useState("");
  const [platform, setPlatform] = useState("TikTok");
  const [followers, setFollowers] = useState("");
  const [error, setError] = useState("");
  const [showCountryPicker, setShowCountryPicker] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (loginMethod === "email" && !email) { setError("Please enter your email address."); return; }
    if (loginMethod === "phone" && !phone) { setError("Please enter your phone number."); return; }
    if (!password) { setError("Please enter your password."); return; }
    if (mode === "signup" && !name) { setError("Please enter your name."); return; }
    if (password.length < 6) { setError("Password must be at least 6 characters."); return; }
    const identifier = loginMethod === "email" ? email : `${countryCode}${phone}`;
    onAuth({
      name: mode === "signup" ? name : (loginMethod === "email" ? email.split("@")[0] : `User${phone.slice(-4)}`),
      email: loginMethod === "email" ? email : `${countryCode}${phone}@phone.crownglobal.com`,
      role,
      phone: loginMethod === "phone" ? `${countryCode}${phone}` : undefined,
    });
  };

  const selectedCountry = countryCodes.find(c => c.code === countryCode) || countryCodes[0];

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md p-7 relative max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer">
          <i className="ri-close-line text-xl"></i>
        </button>

        <div className="flex justify-center mb-4">
          <img
            src="https://static.readdy.ai/image/3fc89005e5f8f541d0781d07714068dd/b31ed3570265bb91107b593ba40da4ad.jpeg"
            alt="CrownGlobal"
            className="h-10 w-auto object-contain rounded"
          />
        </div>

        <h2 className="text-xl font-extrabold text-gray-900 text-center mb-1">
          {mode === "login" ? "Welcome Back" : "Create Account"}
        </h2>
        <p className="text-gray-400 text-sm text-center mb-5">
          {mode === "login" ? "Sign in to your dropshipping account" : "Join CrownGlobal Dropshipping"}
        </p>

        {/* Role selector */}
        <div className="grid grid-cols-3 gap-2 mb-5">
          {roleOptions.map((r) => (
            <button
              key={r.key}
              type="button"
              onClick={() => setRole(r.key)}
              className={`flex flex-col items-center gap-1 py-3 px-2 rounded-xl border-2 transition-all cursor-pointer ${
                role === r.key ? r.color : "border-gray-200 hover:border-gray-300"
              }`}
            >
              <i className={`${r.icon} text-xl`}></i>
              <span className="text-xs font-bold leading-tight text-center">{r.label}</span>
              <span className="text-xs text-gray-400 text-center leading-tight hidden sm:block">{r.desc}</span>
            </button>
          ))}
        </div>

        {/* Login method toggle */}
        <div className="flex bg-gray-100 rounded-full p-1 mb-5">
          <button
            type="button"
            onClick={() => setLoginMethod("email")}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer ${loginMethod === "email" ? "bg-white text-gray-800" : "text-gray-500"}`}
          >
            <i className="ri-mail-line"></i> Email
          </button>
          <button
            type="button"
            onClick={() => setLoginMethod("phone")}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer ${loginMethod === "phone" ? "bg-white text-gray-800" : "text-gray-500"}`}
          >
            <i className="ri-phone-line"></i> Phone
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-3.5">
          {mode === "signup" && (
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Full Name *</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your full name"
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors"
              />
            </div>
          )}

          {mode === "signup" && role === "seller" && (
            <>
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1 block">Factory / Company Name</label>
                <input
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  placeholder="e.g. Shenzhen Electronics Co., Ltd."
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1 block">Warehouse Location</label>
                <select
                  value={warehouseLocation}
                  onChange={(e) => setWarehouseLocation(e.target.value)}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 cursor-pointer bg-white"
                >
                  <option value="">Select warehouse location</option>
                  {["Los Angeles, CA", "New York, NY", "Miami, FL", "Toronto, ON", "Vancouver, BC", "London, UK", "Frankfurt, Germany", "Rotterdam, Netherlands", "Paris, France", "Dubai, UAE", "Sydney, Australia", "Singapore", "Tokyo, Japan", "São Paulo, Brazil"].map(w => (
                    <option key={w}>{w}</option>
                  ))}
                </select>
              </div>
            </>
          )}

          {mode === "signup" && role === "influencer" && (
            <>
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1 block">Primary Platform *</label>
                <div className="grid grid-cols-3 gap-2">
                  {["Facebook", "TikTok", "YouTube", "Twitter/X", "Instagram"].map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setPlatform(p)}
                      className={`py-2 px-2 rounded-xl border text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                        platform === p ? "border-amber-400 bg-amber-50 text-amber-600" : "border-gray-200 text-gray-500 hover:border-amber-200"
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1 block">Followers Count</label>
                <input
                  value={followers}
                  onChange={(e) => setFollowers(e.target.value)}
                  placeholder="e.g. 500K, 1.2M"
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors"
                />
              </div>
            </>
          )}

          {/* Email or Phone input */}
          {loginMethod === "email" ? (
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Email Address *</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors"
              />
            </div>
          ) : (
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Phone Number *</label>
              <div className="flex gap-2">
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowCountryPicker(!showCountryPicker)}
                    className="flex items-center gap-1.5 border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white cursor-pointer hover:border-amber-400 transition-colors whitespace-nowrap"
                  >
                    <span>{selectedCountry.flag}</span>
                    <span className="font-semibold text-gray-700">{countryCode}</span>
                    <i className="ri-arrow-down-s-line text-gray-400 text-xs"></i>
                  </button>
                  {showCountryPicker && (
                    <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-10 max-h-48 overflow-y-auto w-44">
                      {countryCodes.map((c, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => { setCountryCode(c.code); setShowCountryPicker(false); }}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-amber-50 cursor-pointer transition-colors text-left"
                        >
                          <span>{c.flag}</span>
                          <span className="text-gray-600">{c.name}</span>
                          <span className="text-gray-400 ml-auto">{c.code}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="Phone number"
                  className="flex-1 border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors"
                />
              </div>
            </div>
          )}

          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1 block">Password *</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Min. 6 characters"
              className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors"
            />
          </div>

          {error && <p className="text-red-500 text-xs bg-red-50 px-3 py-2 rounded-lg">{error}</p>}

          <button
            type="submit"
            className="bg-amber-400 hover:bg-amber-500 text-white font-bold py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap mt-1"
          >
            {mode === "login" ? "Sign In" : "Create Account"}
          </button>
        </form>

        <p className="text-center text-sm text-gray-500 mt-4">
          {mode === "login" ? "Don't have an account? " : "Already have an account? "}
          <button
            onClick={() => setMode(mode === "login" ? "signup" : "login")}
            className="text-amber-500 font-semibold hover:text-amber-600 cursor-pointer"
          >
            {mode === "login" ? "Sign Up" : "Sign In"}
          </button>
        </p>
      </div>
    </div>
  );
}
